import React, { useState, forwardRef, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import RollingNumber from '../Loaders/RollingNumber';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';

const LinkIcon = 'https://assets.askhippo.co.za/Icons/common/Union_Black.svg';

const infoRows = [
  { label: 'Waiting Periods', key: 'WaitingPeriod' },
  { label: 'Co - Payments', key: 'CoPayments' },
  { label: 'Annual Limit', key: 'AnnualLimit' },
  { label: 'Casualty Ward', key: 'CasualtyWard' },
  { label: 'Hospital Exclusions', key: 'HospitalExclusions' },
];

const BuyIconPink = 'https://assets.askhippo.co.za/Icons/common/Union_Pink.svg';

const renderValueWithBoldCurrency = (text) => {
  if (!text) return null;

  const str = String(text);
  // Match SA Rand currency patterns like "R210 580" or "R22,000"
  const match = str.match(/(R\s?\d[\d\s,.,]*)/i); // removed unnecessary escape for dot

  if (match) {
    const amount = match[1];
    const parts = str.split(amount);
    return (
      <>
        {parts[0]}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {amount}
        </Box>
        {parts[1]}
      </>
    );
  }

  return str;
};

const MobileProviderTileComponent_MedicalGap = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      onBuyOnline,
      buyNowLoading,
      buyNowLoadingForQuotePk,
      bestDealContentReady = true,
      wasLoadingOnMount = useRef(!bestDealContentReady),
    },
    ref
  ) => {
    const { CompanyName, LogoURL, TotalPremium } = data;

    const [isExpanded, setIsExpanded] = useState(false);
    const isBestDealLoading = data.bestDeal && !bestDealContentReady;
    const hasBuyNow = Boolean(data.HasBuyOnline || data.OnlinePremium);

    // case statement needed
    let derivedShortUSP = '';

    if (data.CoverType === 'comprehensive') {
      derivedShortUSP = `Comprehensive Gap Cover`;
    } else if (data.CoverType === 'basic') {
      derivedShortUSP = `Basic Gap Cover`;
    } else if (data.CoverType === 'intermediate') {
      derivedShortUSP = `Intermediate Gap Cover`;
    } else {
      derivedShortUSP = 'Gap Cover';
    }
    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    const handleBuyOnline = (evt) => {
      return onBuyOnline(evt, { action: 'buy-online', data });
    };
    return (
      <Box
        ref={ref}
        sx={{
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          margin: { xs: '0 16px 16px 16px', sm: '0 0 16px 0' },
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '1px solid #2DC8CB' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          position: 'relative',
        }}
      >
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0, // makes it stretch across the box’s full width
              height: '40px',
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // center the text horizontally
              boxSizing: 'border-box',
              zIndex: 1, // ensure it stays above the rest
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.25rem', // 28px
                lineHeight: '1.5rem',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              {bestDealContentReady ? (
                'Your Best Deal!'
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            padding: '16px',
            width: '100%',
            boxSizing: 'border-box',
            mt: data.bestDeal && initialLoadDone ? '40px' : 0,
          }}
        >
          {/* Expand/Collapse Button */}
          <Button
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              position: 'absolute',
              top: `${data.bestDeal && initialLoadDone ? 71 : 20}px`,
              right: '16px',
              width: '48px',
              height: '48px',
              padding: '8px',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{
                fontWeight: 500,
                fontSize: '1.5rem', // Same size as before
                color: '#000',
                transform: isExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s ease',
                display: 'inline-block',
              }}
            >
              keyboard_arrow_down
            </span>
          </Button>

          {/* Logo / Image */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={LogoURL}
                alt={CompanyName}
                style={{
                  maxHeight: '48px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          {/* Provider Name */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                mt: '12px',
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1rem', // 16px
                color: '#1E354E',
              }}
            >
              {CompanyName}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Content */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '0.875rem', // 14px
                color: '#1E354E',
              }}
            >
              {derivedShortUSP}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Divider */}
          <Box
            sx={{
              mt: '12px',
              width: '100%',
              height: '1px',
              backgroundColor: '#E0E0E0',
            }}
          />

          {/* Additional Info Pill */}
          {initialLoadDone &&
            Array.isArray(data.RetailChips) &&
            data.RetailChips.length > 0 && (
              <Box
                sx={{
                  mt: '12px',
                  backgroundColor: data.RetailChipColor,
                  borderRadius: '4px',
                  px: '0px',
                  py: '0px',
                  display: 'inline-block',
                }}
              >
                {data.RetailChips.map((chip, idx) => (
                  <Box key={idx}>
                    <SkeletonLoaderWrapper loading={isBestDealLoading}>
                      <Box
                        key={idx}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#162F3B',
                          backgroundColor: chip.color,
                          letterSpacing: '-0.02em',
                          lineHeight: '1.125rem',
                          borderRadius: '6px',
                          px: '8px',
                          py: '2px',
                          display: 'inline-block',
                        }}
                      >
                        {chip.text}
                      </Box>
                    </SkeletonLoaderWrapper>
                  </Box>
                ))}
              </Box>
            )}

          {/* Bottom Row: Premium/Excess on the left, CTA button on the right */}
          <Box
            sx={{
              mt:
                initialLoadDone && data.RetailChips.length > 0 ? '0px' : '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end', // align bottom edges
              width: '100%',
            }}
          >
            {/* Premium and Excess */}
            <Box>
              <Typography
                sx={{
                  mt: '8px',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  color: '#A4ACB0',
                }}
              >
                From
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                }}
              >
                <RollingNumber
                  value={TotalPremium}
                  prefix={{
                    text: 'R',
                  }}
                  digitSx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '2rem',
                    color: '#162F3B',
                    letterSpacing: '-0.02em',
                  }}
                  suffix={{
                    text: 'pm',
                    sx: {
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      ml: '0px',
                      color: '#162F3B',
                    },
                  }}
                  wrapperSx={{}}
                  extraLoops={3}
                  playSignal={
                    data.bestDeal && wasLoadingOnMount.current ? 1 : 0
                  }
                  animate={data.bestDeal && wasLoadingOnMount.current}
                />
              </Box>
            </Box>

            {/* CTA Button(s) — bottom aligned with Excess */}
            {hasBuyNow ? (
              /* Two CTAs — Buy Now + I'm Interested (matches Life/Funeral) */
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Button
                  onClick={handleBuyOnline}
                  disabled={buyNowLoading || !initialLoadDone}
                  aria-label="Buy Now"
                  sx={{
                    height: '40px',
                    minWidth: 0,
                    borderRadius: '18px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #FF0074',
                    boxShadow: '0px 1px 2px 0px #1018280D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: '12px',
                    '&:hover': { backgroundColor: '#f4eeee' },
                  }}
                >
                  {buyNowLoading && buyNowLoadingForQuotePk === data.QuotePK ? (
                    <LoadingDots variant="buy-now-pink" />
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#FF0074',
                          mr: '4px',
                        }}
                      >
                        Buy
                      </Typography>
                      <Box
                        component="img"
                        src={BuyIconPink}
                        alt="Buy Now"
                        sx={{ width: '18px', height: '18px', display: 'block' }}
                      />
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSelectQuote}
                  disabled={buyNowLoading || isBestDealLoading}
                  sx={{
                    height: '40px',
                    backgroundColor: '#FF0074',
                    borderRadius: '18px',
                    minWidth: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: '16px',
                    py: '8px',
                    '&:hover': { backgroundColor: '#e60067' },
                    ...(isBestDealLoading && {
                      backgroundColor: '#D5D5D5 !important',
                      borderColor: '#D5D5D5 !important',
                      color: '#7A7A7A !important',
                      cursor: 'not-allowed',
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: '#D5D5D5 !important' },
                    }),
                  }}
                >
                  <span
                    className="material-symbols-rounded"
                    style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                  >
                    double_arrow
                  </span>
                </Button>
              </Box>
            ) : (
              /* Single CTA — no buy-now available */
              <Button
                onClick={handleSelectQuote}
                disabled={buyNowLoading}
                sx={{
                  height: '40px',
                  backgroundColor: '#FF0074',
                  borderRadius: '18px',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: '16px',
                  py: '8px',
                  '&:hover': { backgroundColor: '#e60067' },
                  ...(isBestDealLoading && {
                    backgroundColor: '#D5D5D5 !important',
                    borderColor: '#D5D5D5 !important',
                    color: '#7A7A7A !important',
                    cursor: 'not-allowed',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#D5D5D5 !important',
                    },
                  }),
                }}
              >
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                >
                  double_arrow
                </span>
              </Button>
            )}
          </Box>

          {isExpanded && (
            <Box
              sx={{
                mt: '16px', // space above the expanded section
                display: 'flex',
                flexDirection: 'column',
                gap: '16px', // spacing between sections
              }}
            >
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* Description */}
              {data.Description && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '1rem', // 16px
                      color: '#1E354E',
                    }}
                  >
                    Description
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '1rem', // 16px
                      color: '#1E354E',
                      mt: '8px',
                    }}
                  >
                    {data.Description}
                  </Typography>
                  {data?.ReadMoreURL &&
                    String(data.ReadMoreURL).trim() !== '' && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mt: '12px',
                        }}
                      >
                        <Button
                          component="a"
                          href={data.ReadMoreURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          sx={{
                            backgroundColor: '#E3E6E8',
                            borderRadius: '18px',
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mt: '6px',
                            px: '16px',
                            py: '8px',
                            '&:hover': { backgroundColor: '#d0d0d0' },
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontWeight: 600,
                              px: 0,
                              fontSize: '1rem',
                              mr: '4px',
                            }}
                          >
                            Details
                          </Typography>
                          <Box
                            component="img"
                            src={LinkIcon}
                            alt="Link"
                            sx={{
                              mx: '4px',
                              mb: '2px',
                              width: '18px',
                              height: '18px',
                            }}
                          />
                        </Button>
                      </Box>
                    )}
                </Box>
              )}
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* ===== 6) Bottom group: Benefits/Features | | Reviews ===== */}
              <Box sx={{ pl: '0px', py: '0px' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    rowGap: '12px',
                  }}
                >
                  {infoRows
                    .map(({ label, key }) => ({
                      label,
                      value: data && data[key],
                    }))
                    .filter(
                      ({ value }) =>
                        value != null && String(value).trim() !== ''
                    )
                    .map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          backgroundColor: '#F6F6F6',
                          border: '1px solid #E1E1E1',
                          borderRadius: '12px',
                          width: '100%',
                          maxWidth: '869px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          px: '16px',
                          py: '12px',
                          boxSizing: 'border-box',
                        }}
                      >
                        {/* Header */}
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            lineHeight: '1.275rem',
                            color: '#162F3B',
                            mb: '4px',
                          }}
                        >
                          {label}
                        </Typography>

                        {/* Value (Tooltip wrapped) */}
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            lineHeight: '1.275rem',
                            color: '#162F3B',
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {renderValueWithBoldCurrency(value)}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);
MobileProviderTileComponent_MedicalGap.displayName =
  'MobileProviderTileComponent_MedicalGap';
export default MobileProviderTileComponent_MedicalGap;
